/**
 * Single source of truth for entity query keys and their cross-entity cache
 * invalidations.
 *
 * Each entry pairs an entity's own query-key factory (`key`) with the set of
 * related query keys to invalidate when that entity changes (`invalidates`).
 * Co-locating both means a query key and the things that depend on it cannot
 * drift apart: `queryKeys` builds its entity factories from `key` here, and the
 * cache-invalidation layer reads `invalidates` (plus `key`) from here.
 *
 * `key` is `undefined` for entities that participate in invalidations only by
 * table name (e.g. `actuator_rules`, `threshold_rules`, `profile_stations`) and
 * therefore do not contribute an own query key.
 *
 * The string tuples below are intentionally written as literals so this module
 * has no imports and cannot form an import cycle with `queryKeys`. They are
 * value-identical to the keys produced by the corresponding `key` factories.
 */

type KeyFactory = () => readonly string[];

interface EntityMetaEntry {
  /** The entity's own query-key factory, or `undefined` for table-only entities. */
  key: KeyFactory | undefined;
  /** Related query keys to invalidate when this entity is modified. */
  invalidates: string[][];
}

export const entityMeta = {
  // When crops are modified, invalidate these related entities
  crops: {
    key: () => ['crops'] as const,
    invalidates: [
      ['stations'], // stations have crop_id
      ['cameras'], // cameras have crop_id
    ],
  },

  // When stations are modified, invalidate these
  stations: {
    key: () => ['stations'] as const,
    invalidates: [
      ['groups'], // groups belong to stations
      ['sensors'], // sensors belong to stations
      ['devices'], // devices belong to stations
      ['crops'], // for displaying associated crop info
    ],
  },

  // When groups are modified, invalidate these
  groups: {
    key: () => ['groups'] as const,
    invalidates: [
      ['sensors'], // sensors can belong to groups
      ['stations'], // for group counts in stations
    ],
  },

  // When sensors are modified, invalidate these
  sensors: {
    key: () => ['sensors'] as const,
    invalidates: [
      ['groups'], // for sensor counts in groups
      ['stations'], // for sensor counts in stations
      ['threshold_rules'], // threshold rules reference sensors
      ['actuator_rules'], // actuator rules reference sensors
    ],
  },

  // When devices are modified, invalidate these
  devices: {
    key: () => ['devices'] as const,
    invalidates: [
      ['stations'], // devices belong to stations
      ['actuators'], // actuators belong to devices
    ],
  },

  // When actuators are modified, invalidate these
  actuators: {
    key: () => ['actuators'] as const,
    invalidates: [
      ['devices'], // for actuator counts in devices
      ['actuator_rules'], // actuator rules reference actuators
    ],
  },

  // When sensor types are modified, invalidate these
  sensorTypes: {
    key: () => ['sensorTypes'] as const,
    invalidates: [
      ['sensors'], // sensors have sensor_type_id
      ['threshold_rules'], // for displaying sensor type info
    ],
  },

  // When measurement units are modified, invalidate these
  measurementUnits: {
    key: () => ['measurementUnits'] as const,
    invalidates: [
      ['sensors'], // sensors have unit_id
      ['sensorTypes'], // sensor types have default unit
    ],
  },

  // When actuator types are modified, invalidate these
  actuatorTypes: {
    key: () => ['actuatorTypes'] as const,
    invalidates: [
      ['actuators'], // actuators have actuator_type_id
    ],
  },

  // When crop types are modified, invalidate these
  cropTypes: {
    key: () => ['cropTypes'] as const,
    invalidates: [
      ['crops'], // crops have crop_type_id
    ],
  },

  // When cameras are modified, invalidate these
  cameras: {
    key: () => ['cameras'] as const,
    invalidates: [
      ['crops'], // for camera counts in crops
      ['cameras'], // invalidate all camera queries
    ],
  },

  // When actuator rules are modified, invalidate these
  actuator_rules: {
    key: undefined,
    invalidates: [
      ['actuators'], // for rule counts in actuators
      ['sensors'], // for rule associations in sensors
    ],
  },

  // When threshold rules are modified, invalidate these
  threshold_rules: {
    key: undefined,
    invalidates: [
      ['sensors'], // for rule counts in sensors
    ],
  },

  // When profile_stations (memberships) are modified, invalidate these
  profile_stations: {
    key: undefined,
    invalidates: [
      ['userMemberships'], // user's membership data
      ['stations'], // stations may show member counts
      ['profiles'], // profiles with membership information
    ],
  },
} satisfies Record<string, EntityMetaEntry>;

export type EntityName = keyof typeof entityMeta;
